import { FormatedGraphData } from "./histogram";
import * as tradsOriginal from "../../../locales/fr-FR/translations.json";
import { IndicatorIds } from "types/enums/IndicatorsIds";
import { AcvData, UsageAcvData } from "types/other/acvData";
import { DetailledSheet } from "types/other/projectZone";
import { AllSheetsFromProject } from "types/other/pdfParsingData";
import { AllotmentsIds, SheetType } from "types/noocoEnums";

const trads = JSON.parse(JSON.stringify(tradsOriginal));

interface IACV {
  processDataGraph(
    indicatorId: number,
    allotmentId: number,
    allSheets: AllSheetsFromProject
  ): {
    graphData: FormatedGraphData[];
    graphKeys: string[];
    impactTotal: number;
  };
}

function getSheetImpactDataDependingOnIndicatorMode(
  indicatorId: number,
  sheet: DetailledSheet
): AcvData {
  let useStatic = false;

  if (indicatorId === IndicatorIds.STATIC) {
    useStatic = true;
    indicatorId = IndicatorIds.DYNAMIC;
  }

  const acvData: AcvData =
    useStatic || indicatorId !== IndicatorIds.DYNAMIC
      ? sheet.ACV[indicatorId].static
      : sheet.ACV[indicatorId].dynamic;

  return acvData;
}

function returnSheetDependingOnAllotmentId(
  allotmentId: number,
  sheet: DetailledSheet,
  isCertified: boolean = false
) {
  let currentSheet: any = sheet;

  if (isCertified && sheet.ReEqs && sheet.ReEqs[0]) {
    if (sheet.ReEqs[0].Re2020Id !== sheet.id) {
      currentSheet = sheet.ReEqs[0].Re2020;
    }
  }
  return currentSheet;
}

function agreagateDataset(
  datasets: FormatedGraphData[],
  key: string,
  impactDetails: any
) {
  datasets[0] = { ...datasets[0], [key]: impactDetails.prod };
  datasets[1] = { ...datasets[1], [key]: impactDetails.edif };
  datasets[2] = { ...datasets[2], [key]: impactDetails.end };
}

function addModuleDToDataSet(
  datasets: FormatedGraphData[],
  key: string,
  usageRes: UsageAcvData
) {
  modifyOrPushDataset(
    datasets,
    trads["APP.LIFECYCLE.LIFECYCLETAB.MODULE_D_SECOND"],
    key,
    usageRes.moduleD
  );
}

function modifyOrPushDataset(
  array: FormatedGraphData[],
  keyToCompare: string,
  keyToAdd: string,
  value: number,
  dataToSplice?: { startIndex: number; deleteCount: number }
) {
  const index = array.findIndex(
    (obj: FormatedGraphData) => obj.name === keyToCompare
  );
  if (index === -1) {
    const newDataForGraph: FormatedGraphData = {
      name: keyToCompare,
      [keyToAdd]: value,
    };
    if (dataToSplice) {
      array.splice(
        dataToSplice.startIndex,
        dataToSplice.deleteCount,
        newDataForGraph
      );
    } else {
      array.push(newDataForGraph);
    }
  } else {
    array[index] = { ...array[index], [keyToAdd]: value };
  }
}

function managementOfBImpact(
  datasets: FormatedGraphData[],
  key: string,
  usageRes: any
) {
  // if no exploitation, or the B indice is set, only show one exploitation bar
  if (
    usageRes.b1 +
      usageRes.b2 +
      usageRes.b1fluide +
      usageRes.b3 +
      usageRes.b4 +
      usageRes.b4fluide +
      usageRes.b6 +
      usageRes.b7 +
      usageRes.bforfait ===
      0 ||
    usageRes.b
  ) {
    // Even if there is now exploitation, show a bar. So i just need to
    // put any value in it, which will always be 0

    modifyOrPushDataset(datasets, "Exploitation", key, usageRes.b || 0, {
      startIndex: 2,
      deleteCount: 0,
    });
  } else {
    const entretienImpact =
      usageRes.bforfait +
      usageRes.b1EquipFluid +
      usageRes.b1fluide +
      usageRes.b2 +
      usageRes.b3 +
      usageRes.b4 +
      usageRes.b4fluide;

    modifyOrPushDataset(
      datasets,
      trads["APP.LIFECYCLE.LIFECYCLETAB.MAINTENANCE"],
      key,
      entretienImpact,
      { startIndex: 2, deleteCount: 0 }
    );

    const operationImpact = usageRes.b6 + usageRes.b7;

    modifyOrPushDataset(
      datasets,
      trads["APP.LIFECYCLE.LIFECYCLETAB.OPERATION"],
      key,
      operationImpact,
      { startIndex: 2, deleteCount: 0 }
    );
  }
}

const initImpactDetails = () => ({
  prod: 0,
  edif: 0,
  end: 0,
  endfluide: 0,
  edifChantier: 0,
});

const initForfaitRes = () => ({
  prod: 0,
  edif: 0,
  end: 0,
  edifChantier: 0,
  moduleD: 0,
});

const initUsageImpact = () => ({
  bforfait: 0,
  b1: 0,
  b2: 0,
  b3: 0,
  b4: 0,
  b6: 0,
  b7: 0,
  moduleD: 0,
  b1fluide: 0,
  b4fluide: 0,
  b1EquipFluid: 0,
});
export class Acv implements IACV {
  constructor() {}

  processDataGraph(
    indicatorId: number,
    allotmentId: number,
    allSheets: AllSheetsFromProject
  ) {
    const datasets: FormatedGraphData[] = [
      {
        name: trads["APP.LIFECYCLE.LIFECYCLETAB.PRODUCTION"],
      },
      {
        name: trads["APP.LIFECYCLE.LIFECYCLETAB.EDIFICATION"],
      },
      {
        name: trads["APP.LIFECYCLE.LIFECYCLETAB.LIFETIME"],
      },
    ];
    let graphKeys: string[] = [];

    let total = 0;

    let isCertified: boolean = [
      AllotmentsIds.RE2020,
      AllotmentsIds.EPCM,
    ].includes(allotmentId);

    for (const [key, value] of Object.entries(allSheets)) {
      const impactDetails: any = initImpactDetails();
      const forfaitRes: any = initForfaitRes();

      let usageImpact: any = initUsageImpact();

      value?.forEach((originalSheet) => {
        const currentSheet = returnSheetDependingOnAllotmentId(
          allotmentId,
          originalSheet,
          isCertified
        );
        const acvData: AcvData = getSheetImpactDataDependingOnIndicatorMode(
          indicatorId,
          currentSheet
        );

        const baseRes = acvData.base;
        const usage = acvData.usage;

        for (const [key, value] of Object.entries(baseRes)) {
          if (
            currentSheet.type_fiche === SheetType.FORFAIT &&
            !key.toLocaleLowerCase().includes("fluide")
          ) {
            forfaitRes[key] += Number(value);
          }
          impactDetails[key] += Number(value);

          total += Number(value);
        }

        for (const [key, value] of Object.entries(usage)) {
          usageImpact[key] += Number(value);
          total += Number(value);
        }
      });

      agreagateDataset(datasets, key, impactDetails);

      if (isCertified) {
        addModuleDToDataSet(datasets, key, usageImpact);
      }

      managementOfBImpact(datasets, key, usageImpact);

      graphKeys.push(key);
    }

    return {
      graphData: datasets,
      graphKeys: graphKeys,
      impactTotal: total,
    };
  }
}
